import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getOtEvalNoteForm,
  updateOtEvalNoteForm,
} from "../../../../../services/patientService";
import {
  DeleteIcon,
  FileCheckIcon,
  HomeIcon,
  MedicalNotesIcon,
  PlusIcon,
  RunningIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { PATIENT_THEME } from "../../../patientTheme";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  OT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ADL_ASSIST_LEVELS,
  ADL_TASKS,
  DOMINANT_HAND_OPTIONS,
  ENDURANCE_FLAG_OPTIONS,
  ENTRANCE_ACCESS_OPTIONS,
  GOOD_FAIR_POOR_OPTIONS,
  IDENTIFIED_DEFICIT_OPTIONS,
  LIVES_WITH_OPTIONS,
  MENTAL_COGNITIVE_OPTIONS,
  OT_EVAL_ARRAY_KEYS,
  OT_INTERVENTION_OPTIONS,
  REHAB_POTENTIAL_OPTIONS,
  RESIDENCE_TYPE_OPTIONS,
  SPLINT_EQUIPMENT_OPTIONS,
  UE_JOINT_MOTIONS,
  WFL_DEFICIT_OPTIONS,
  WFL_IMPAIRED_OPTIONS,
  YES_NO_OPTIONS,
  coerceExclusiveChoice,
  emptyAdlRows,
  emptyOtEvalPayload,
  emptyUeFunctionRows,
  toggleInList,
  toggleSplintEquipment,
} from "./otEvalNoteContent";
import {
  emptyTextGoalRow,
  normalizeTextGoals,
  renumberTextGoals,
  serializeTextGoals,
} from "../ptNotes/ptGoals";
import OtNotesCommonHeader from "./OtNotesCommonHeader";
import PainScalePills from "../ptNotes/PainScalePills";
import {
  normalizeOtCommonHeaderState,
  resolveOtNotesCommonHeader,
  withNormalizedOtCommonHeader,
} from "./otNotesCommonHeaderUtils";
import {
  assertOtVisitMetaForFlag,
  buildOtVisitMetaProps,
  pickOtVisitMeta,
} from "./otVisitCommon";

const EVAL_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyOtEvalPayload(),
  evalDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const SelectField = ({ value, options, canEdit, onChange, placeholder }) => (
  <select
    className={fieldClass}
    value={value || ""}
    disabled={!canEdit}
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="">{placeholder || "—"}</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

SelectField.propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

/** Equal-height field cell: stretches with grid row peers. */
const StretchField = ({ label, children, minTextareaClass = "min-h-[4.5rem]" }) => (
  <Field label={label} span="flex h-full min-h-0 flex-col">
    {React.isValidElement(children)
      ? React.cloneElement(children, {
          className: `${children.props.className || ""} ${minTextareaClass} h-full flex-1`.trim(),
        })
      : children}
  </Field>
);

StretchField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  minTextareaClass: PropTypes.string,
};

/** Red tint when MMT looks weak or AROM looks limited (simple clinical cue). */
const looksAbnormal = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return false;
  if (/^[0-2]([+/]-)?(\/5)?$/i.test(s) || /^3-?\/5$/i.test(s) || /^3\+\/5$/i.test(s))
    return true;
  if (/^-?\d+/.test(s) && Number.parseInt(s, 10) < 0) return true;
  return false;
};

const OtEvalNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyOtEvalPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const byJoint = Object.fromEntries(
        (Array.isArray(loadedPayload.ueFunction) ? loadedPayload.ueFunction : []).map(
          (row) => [row.joint, row],
        ),
      );
      const ueFunction = UE_JOINT_MOTIONS.map((joint) => ({
        ...emptyUeFunctionRows().find((r) => r.joint === joint),
        ...(byJoint[joint] || {}),
        joint,
      }));

      const byTask = Object.fromEntries(
        (Array.isArray(loadedPayload.adlPerformance)
          ? loadedPayload.adlPerformance
          : []
        ).map((row) => [row.task, row]),
      );
      const adlPerformance = ADL_TASKS.map((task) => ({
        ...emptyAdlRows().find((r) => r.task === task),
        ...(byTask[task] || {}),
        task,
        level: byTask[task]?.level || "",
      }));

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickOtVisitMeta(loadedPayload, data),
        evalDate: data.evalDate || loadedPayload.evalDate || data.visitDate || today(),
        commonHeader: normalizeOtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        ueFunction,
        adlPerformance,
        shortTermGoals: normalizeTextGoals(loadedPayload, {
          arrayKey: "shortTermGoals",
          legacyPrefix: "shortTermGoal",
          kind: "st",
          minCount: 1,
        }),
        longTermGoals: normalizeTextGoals(loadedPayload, {
          arrayKey: "longTermGoals",
          legacyPrefix: "longTermGoal",
          kind: "lt",
          minCount: 1,
        }),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      OT_EVAL_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : [];
      });

      merged.livesWith = coerceExclusiveChoice(
        loadedPayload.livesWith ?? base.livesWith,
      );
      merged.residenceType = coerceExclusiveChoice(
        loadedPayload.residenceType ?? base.residenceType,
      );

      if (merged.painLevel != null && merged.painLevel !== "") {
        merged.painLevel = Number(merged.painLevel);
        if (Number.isNaN(merged.painLevel)) merged.painLevel = null;
      }

      for (let n = 1; n <= 12; n += 1) {
        delete merged[`shortTermGoal${n}`];
        delete merged[`longTermGoal${n}`];
      }

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveOtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Occupational Therapy Evaluation Note",
            formType: clinicalForm.formType || draft?.formType || "ot_eval_note",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            evalDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyOtEvalPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getOtEvalNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveOtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const toggleSplints = (item) => {
    setValues((prev) => ({
      ...prev,
      splintsEquipment: toggleSplintEquipment(prev.splintsEquipment, item),
    }));
  };

  const updateUeFunction = (joint, patch) => {
    setValues((prev) => ({
      ...prev,
      ueFunction: (prev.ueFunction || []).map((row) =>
        row.joint === joint ? { ...row, ...patch } : row,
      ),
    }));
  };

  const updateAdlLevel = (task, level) => {
    setValues((prev) => ({
      ...prev,
      adlPerformance: (prev.adlPerformance || []).map((row) =>
        row.task === task ? { ...row, level } : row,
      ),
    }));
  };

  const updateTextGoal = (listKey, goalId, text) => {
    setValues((prev) => ({
      ...prev,
      [listKey]: (prev[listKey] || []).map((row) =>
        row.id === goalId ? { ...row, text } : row,
      ),
    }));
  };

  const addTextGoal = (listKey, kind) => {
    setValues((prev) => {
      const current = prev[listKey] || [];
      return {
        ...prev,
        [listKey]: renumberTextGoals(
          [...current, emptyTextGoalRow(current.length + 1, kind)],
          kind,
        ),
      };
    });
  };

  const removeTextGoal = (listKey, kind, goalId) => {
    setValues((prev) => {
      const current = prev[listKey] || [];
      if (current.length <= 1) return prev;
      return {
        ...prev,
        [listKey]: renumberTextGoals(
          current.filter((row) => row.id !== goalId),
          kind,
        ),
      };
    });
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        OT_FORM_EDITOR_ROLES,
        "View only — only OT/OTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType:
      form?.formType || draft?.formType || clinicalForm?.formType || "ot_eval_note",
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      evalDate,
      ...otFields
    } = values;

    const nestedPayload = withNormalizedOtCommonHeader({
      ...otFields,
      anticipatedDischargeDate: values.anticipatedDischargeDate
        ? formatDateForInput(values.anticipatedDischargeDate)
        : "",
      ueFunction: (values.ueFunction || []).map((row) => ({
        joint: row.joint,
        aromRight: row.aromRight || "",
        aromLeft: row.aromLeft || "",
        mmtRight: row.mmtRight || "",
        mmtLeft: row.mmtLeft || "",
      })),
      adlPerformance: (values.adlPerformance || []).map((row) => ({
        task: row.task,
        level: row.level || "",
      })),
      shortTermGoals: serializeTextGoals(values.shortTermGoals),
      longTermGoals: serializeTextGoals(values.longTermGoals),
    });

    OT_EVAL_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    for (let n = 1; n <= 12; n += 1) {
      delete nestedPayload[`shortTermGoal${n}`];
      delete nestedPayload[`longTermGoal${n}`];
    }

    const payload = {
      flag,
      evalDate: evalDate ? formatDateForInput(evalDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertOtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateOtEvalNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.evalDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showInterventionOther = (values.interventions || []).includes("Other");
  const showDeficitOther = (values.identifiedDeficits || []).includes("Others");
  const showSplintsOther = (values.splintsEquipment || []).some(
    (item) => item && item !== "None",
  );
  const tableHeadClass = `text-[9px] font-semibold uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`;

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Occupational Therapy Evaluation Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home Health Clinical Documentation
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn || values.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              values.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Evaluation Date"
          dateValue={displayDateUS(values.evalDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Evaluation Date
                </div>
                <InputDatePicker
                  name="evalDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.evalDate) || ""}
                  onChange={(event) => setValue("evalDate", event.target.value)}
                  max={EVAL_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildOtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        {/* Fixed sections 1–6: Patient / Company / Insurance / Physician / Diagnosis / Medication */}
        <OtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        {/* 7. Living Situation */}
        <Section icon={HomeIcon} title="7. Living Situation">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch">
            <Field label="Residence Type" span="flex h-full flex-col">
              <SelectField
                value={values.residenceType}
                options={RESIDENCE_TYPE_OPTIONS}
                canEdit={canEdit}
                onChange={(v) => setValue("residenceType", v)}
              />
            </Field>
            <Field label="Lives With" span="flex h-full flex-col">
              <SelectField
                value={values.livesWith}
                options={LIVES_WITH_OPTIONS}
                canEdit={canEdit}
                onChange={(v) => setValue("livesWith", v)}
              />
            </Field>
          </div>
          <div className="mt-3 rounded-md border border-sky-100 bg-sky-50/50 p-3 space-y-2.5">
            <div>
              <SubHeading>Entrance Access</SubHeading>
              <ExclusiveChoiceGrid
                name="entranceAccess"
                options={ENTRANCE_ACCESS_OPTIONS}
                value={values.entranceAccess || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("entranceAccess", opt)}
                columns={3}
              />
            </div>
            <div>
              <SubHeading>Railing Present</SubHeading>
              <ExclusiveChoiceGrid
                name="railingPresent"
                options={YES_NO_OPTIONS}
                value={values.railingPresent || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("railingPresent", opt)}
                columns={2}
              />
            </div>
          </div>
        </Section>

        {/* 8. Subjective History */}
        <Section icon={MedicalNotesIcon} title="8. Subjective History">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch">
            <StretchField label="Chief Complaint & Patient Goals">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.chiefComplaintGoals || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("chiefComplaintGoals", e.target.value)}
              />
            </StretchField>
            <StretchField label="History of Present Illness">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.historyPresentIllness || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("historyPresentIllness", e.target.value)
                }
              />
            </StretchField>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch">
            <StretchField label="Prior Level of Function (PLOF)">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.plof || ""}
                disabled={!canEdit}
                placeholder="Describe patient's prior functional status…"
                onChange={(e) => setValue("plof", e.target.value)}
              />
            </StretchField>
            <StretchField label="Current Level of Function / Limitations">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.currentLevelOfFunction || ""}
                disabled={!canEdit}
                placeholder="Describe current function and limitations…"
                onChange={(e) =>
                  setValue("currentLevelOfFunction", e.target.value)
                }
              />
            </StretchField>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 items-stretch">
            <StretchField
              label="Medication History"
              minTextareaClass="min-h-[3.5rem]"
            >
              <textarea
                rows={2}
                className={textareaClass}
                value={values.medicationHistory || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("medicationHistory", e.target.value)}
              />
            </StretchField>
            <StretchField label="Allergies" minTextareaClass="min-h-[3.5rem]">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.allergiesNarrative || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("allergiesNarrative", e.target.value)}
              />
            </StretchField>
            <StretchField
              label="Current Medications"
              minTextareaClass="min-h-[3.5rem]"
            >
              <textarea
                rows={2}
                className={textareaClass}
                value={values.currentMedicationsNarrative || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("currentMedicationsNarrative", e.target.value)
                }
              />
            </StretchField>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch">
            <Field label="Relieving Factors" span="flex h-full flex-col">
              <input
                type="text"
                className={fieldClass}
                value={values.relievingFactors || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("relievingFactors", e.target.value)}
              />
            </Field>
            <Field label="Aggravating Factors" span="flex h-full flex-col">
              <input
                type="text"
                className={fieldClass}
                value={values.aggravatingFactors || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("aggravatingFactors", e.target.value)}
              />
            </Field>
          </div>

          <div className="mt-3">
            <PainScalePills
              label="Pain Assessment (0–10)"
              name="painLevel"
              value={values.painLevel}
              canEdit={canEdit}
              onChange={(v) => setValue("painLevel", v)}
            />
          </div>
        </Section>

        {/* 9. Objective Assessment */}
        <Section icon={RunningIcon} title="9. Objective Assessment">
          {/* A. Mental & Cognitive | Vision & Perception */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
            <div className="rounded-md border border-slate-200 p-2.5 space-y-2 h-full">
              <SubHeading>Mental &amp; Cognitive Status</SubHeading>
              <CheckboxGrid
                options={MENTAL_COGNITIVE_OPTIONS}
                values={values.mentalCognitive}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("mentalCognitive", opt)}
                columns={2}
              />
              <Field label="Comments">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.cognitiveComments || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("cognitiveComments", e.target.value)
                  }
                />
              </Field>
            </div>

            <div className="rounded-md border border-slate-200 p-2.5 space-y-2 h-full">
              <SubHeading>Vision &amp; Perception</SubHeading>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Vision Status
                </p>
                <ExclusiveChoiceGrid
                  name="visionStatus"
                  options={WFL_IMPAIRED_OPTIONS}
                  value={values.visionStatus || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("visionStatus", opt)}
                  columns={2}
                />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Wears Glasses
                </p>
                <ExclusiveChoiceGrid
                  name="wearsGlasses"
                  options={YES_NO_OPTIONS}
                  value={values.wearsGlasses || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("wearsGlasses", opt)}
                  columns={2}
                />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Depth Perception
                </p>
                <ExclusiveChoiceGrid
                  name="depthPerception"
                  options={WFL_DEFICIT_OPTIONS}
                  value={values.depthPerception || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("depthPerception", opt)}
                  columns={2}
                />
              </div>
            </div>
          </div>

          {/* B. Upper Extremity Function */}
          <div className="mt-3 rounded-md border border-slate-200 overflow-hidden">
            <div className="px-2.5 py-1.5 border-b border-slate-200 bg-slate-50">
              <SubHeading>Upper Extremity Function (ROM &amp; Strength)</SubHeading>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-[11px]">
                <thead>
                  <tr className={tableHeadClass}>
                    <th className="px-2 py-1.5">Joint Motion</th>
                    <th className="px-2 py-1.5 text-center">AROM (R)</th>
                    <th className="px-2 py-1.5 text-center">AROM (L)</th>
                    <th className="px-2 py-1.5 text-center">MMT (R)</th>
                    <th className="px-2 py-1.5 text-center">MMT (L)</th>
                  </tr>
                </thead>
                <tbody>
                  {(values.ueFunction || []).map((row) => (
                    <tr key={row.joint} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-medium text-slate-700 whitespace-nowrap">
                        {row.joint}
                      </td>
                      {["aromRight", "aromLeft", "mmtRight", "mmtLeft"].map(
                        (field) => (
                          <td key={field} className="px-1 py-0.5">
                            <input
                              type="text"
                              className={`${fieldClass} text-center tabular-nums ${
                                looksAbnormal(row[field])
                                  ? "text-red-600 border-red-200"
                                  : ""
                              }`}
                              value={row[field] || ""}
                              disabled={!canEdit}
                              onChange={(e) =>
                                updateUeFunction(row.joint, {
                                  [field]: e.target.value,
                                })
                              }
                            />
                          </td>
                        ),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100 p-2.5">
              <Field label="UE Function Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.ueFunctionComments || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("ueFunctionComments", e.target.value)
                  }
                />
              </Field>
            </div>
          </div>

          {/* C. ADL / IADL Performance */}
          <div className="mt-3 rounded-md border border-slate-200 overflow-hidden">
            <div className="px-2.5 py-1.5 border-b border-slate-200 bg-slate-50">
              <SubHeading>
                Activities of Daily Living (ADL/IADL) Performance
              </SubHeading>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-[11px]">
                <thead>
                  <tr className={tableHeadClass}>
                    <th className="px-2 py-1.5">Task</th>
                    {ADL_ASSIST_LEVELS.map((level) => (
                      <th
                        key={level.key}
                        className="px-1 py-1.5 text-center whitespace-nowrap"
                      >
                        {level.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(values.adlPerformance || []).map((row) => (
                    <tr key={row.task} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-medium text-slate-700 whitespace-nowrap">
                        {row.task}
                      </td>
                      {ADL_ASSIST_LEVELS.map((level) => (
                        <td key={level.key} className="px-1 py-0.5 text-center">
                          <input
                            type="radio"
                            name={`adl-${row.task}`}
                            className="h-3.5 w-3.5 accent-indigo-600"
                            checked={row.level === level.key}
                            disabled={!canEdit}
                            onChange={() =>
                              updateAdlLevel(row.task, level.key)
                            }
                            aria-label={`${row.task} ${level.label}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100 p-2.5">
              <Field label="ADL Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.adlComments || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("adlComments", e.target.value)}
                />
              </Field>
            </div>
          </div>

          {/* D. Hand Function | Balance */}
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
            <div className="rounded-md border border-slate-200 p-2.5 space-y-2 h-full">
              <SubHeading>Hand Function</SubHeading>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Dominant Hand
                </p>
                <ExclusiveChoiceGrid
                  name="dominantHand"
                  options={DOMINANT_HAND_OPTIONS}
                  value={values.dominantHand || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("dominantHand", opt)}
                  columns={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Grip LBS (R)">
                  <input
                    type="text"
                    className={`${fieldClass} tabular-nums`}
                    value={values.gripLbsRight || ""}
                    disabled={!canEdit}
                    onChange={(e) => setValue("gripLbsRight", e.target.value)}
                  />
                </Field>
                <Field label="Grip LBS (L)">
                  <input
                    type="text"
                    className={`${fieldClass} tabular-nums`}
                    value={values.gripLbsLeft || ""}
                    disabled={!canEdit}
                    onChange={(e) => setValue("gripLbsLeft", e.target.value)}
                  />
                </Field>
                <Field label="Pinch Lateral (R)">
                  <input
                    type="text"
                    className={`${fieldClass} tabular-nums`}
                    value={values.pinchLateralRight || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("pinchLateralRight", e.target.value)
                    }
                  />
                </Field>
                <Field label="Pinch Lateral (L)">
                  <input
                    type="text"
                    className={`${fieldClass} tabular-nums`}
                    value={values.pinchLateralLeft || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("pinchLateralLeft", e.target.value)
                    }
                  />
                </Field>
                <Field label="Pinch 3-Point (R)">
                  <input
                    type="text"
                    className={`${fieldClass} tabular-nums`}
                    value={values.pinchThreePointRight || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("pinchThreePointRight", e.target.value)
                    }
                  />
                </Field>
                <Field label="Pinch 3-Point (L)">
                  <input
                    type="text"
                    className={`${fieldClass} tabular-nums`}
                    value={values.pinchThreePointLeft || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("pinchThreePointLeft", e.target.value)
                    }
                  />
                </Field>
              </div>
              <Field label="Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.handFunctionComments || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("handFunctionComments", e.target.value)
                  }
                />
              </Field>
            </div>

            <div className="rounded-md border border-slate-200 p-2.5 space-y-2 h-full">
              <SubHeading>Balance</SubHeading>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Sitting
                </p>
                <ExclusiveChoiceGrid
                  name="sittingBalance"
                  options={GOOD_FAIR_POOR_OPTIONS}
                  value={values.sittingBalance || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("sittingBalance", opt)}
                  columns={3}
                />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Standing
                </p>
                <ExclusiveChoiceGrid
                  name="standingBalance"
                  options={GOOD_FAIR_POOR_OPTIONS}
                  value={values.standingBalance || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("standingBalance", opt)}
                  columns={3}
                />
              </div>
              <Field label="Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.balanceComments || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("balanceComments", e.target.value)}
                />
              </Field>
            </div>
          </div>

          {/* E. Endurance | Splints & Equipment */}
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
            <div className="rounded-md border border-slate-200 p-2.5 space-y-2 h-full">
              <SubHeading>Endurance</SubHeading>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Tolerance
                </p>
                <ExclusiveChoiceGrid
                  name="enduranceTolerance"
                  options={GOOD_FAIR_POOR_OPTIONS}
                  value={values.enduranceTolerance || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("enduranceTolerance", opt)}
                  columns={3}
                />
              </div>
              <CheckboxGrid
                options={ENDURANCE_FLAG_OPTIONS}
                values={values.enduranceFlags}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("enduranceFlags", opt)}
                columns={1}
              />
              <Field label="Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.enduranceComments || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("enduranceComments", e.target.value)
                  }
                />
              </Field>
            </div>

            <div className="rounded-md border border-slate-200 p-2.5 space-y-2 h-full">
              <SubHeading>Splints &amp; Equipment</SubHeading>
              <CheckboxGrid
                options={SPLINT_EQUIPMENT_OPTIONS}
                values={values.splintsEquipment}
                canEdit={canEdit}
                onToggle={toggleSplints}
                columns={1}
              />
              {showSplintsOther ? (
                <input
                  type="text"
                  className={fieldClass}
                  value={values.splintsEquipmentOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify other / details…"
                  onChange={(e) =>
                    setValue("splintsEquipmentOther", e.target.value)
                  }
                />
              ) : null}
              <Field label="Comments">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.splintsEquipmentComments || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("splintsEquipmentComments", e.target.value)
                  }
                />
              </Field>
            </div>
          </div>
        </Section>

        {/* 10. Clinical Assessment & Performance Deficits */}
        <Section
          icon={FileCheckIcon}
          title="10. Clinical Assessment & Performance Deficits"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
            <StretchField
              label="Clinical Impression / Summary"
              minTextareaClass="min-h-[10rem]"
            >
              <textarea
                rows={8}
                className={textareaClass}
                value={values.clinicalImpression || ""}
                disabled={!canEdit}
                placeholder="Summarize clinical findings, functional limitations, and skilled OT necessity…"
                onChange={(e) => setValue("clinicalImpression", e.target.value)}
              />
            </StretchField>

            <div className="rounded-md border border-sky-100 bg-sky-50/50 p-3 space-y-2.5 h-full flex flex-col">
              <div>
                <SubHeading>Identified Deficits</SubHeading>
                <CheckboxGrid
                  options={IDENTIFIED_DEFICIT_OPTIONS}
                  values={values.identifiedDeficits}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("identifiedDeficits", opt)}
                  columns={1}
                />
                {showDeficitOther ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.identifiedDeficitsOther || ""}
                    disabled={!canEdit}
                    placeholder="Specify other deficits…"
                    onChange={(e) =>
                      setValue("identifiedDeficitsOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
              <div className="mt-auto">
                <SubHeading>Rehab Potential</SubHeading>
                <ExclusiveChoiceGrid
                  name="rehabPotential"
                  options={REHAB_POTENTIAL_OPTIONS}
                  value={values.rehabPotential || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("rehabPotential", opt)}
                  columns={3}
                />
              </div>
            </div>
          </div>
        </Section>

        {/* 11. Plan of Care */}
        <Section icon={TargetIcon} title="11. Plan of Care">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch">
            <Field label="Visit Frequency" span="flex h-full flex-col">
              <input
                type="text"
                className={fieldClass}
                value={values.visitFrequency || ""}
                disabled={!canEdit}
                placeholder="e.g., 2x per week"
                onChange={(e) => setValue("visitFrequency", e.target.value)}
              />
            </Field>
            <Field
              label="Anticipated Discharge Date"
              span="flex h-full flex-col"
            >
              <DateFieldInput
                name="anticipatedDischargeDate"
                value={values.anticipatedDischargeDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("anticipatedDischargeDate", v)}
              />
            </Field>
          </div>

          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
            <div className="space-y-2">
              <SubHeading>Interventions</SubHeading>
              <CheckboxGrid
                options={OT_INTERVENTION_OPTIONS}
                values={values.interventions}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("interventions", opt)}
                columns={3}
              />
              {showInterventionOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.interventionOther || ""}
                  disabled={!canEdit}
                  placeholder="Other intervention…"
                  onChange={(e) =>
                    setValue("interventionOther", e.target.value)
                  }
                />
              ) : null}
            </div>

            <div className="rounded-md border border-indigo-100 bg-indigo-50/40 p-3 space-y-3">
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Short Term Goal 1 (2 Weeks)
                </p>
                {(values.shortTermGoals || []).map((row, idx) => (
                  <div key={row.id} className="flex gap-2 min-w-0">
                    <span
                      className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-bold text-indigo-700"
                      aria-hidden
                    >
                      {idx + 1}
                    </span>
                    <textarea
                      rows={2}
                      className={`${textareaClass} min-w-0 flex-1`}
                      value={row.text || ""}
                      disabled={!canEdit}
                      placeholder="e.g., Pt will don UB clothing with min A…"
                      aria-label={`Short-term goal ${idx + 1}`}
                      onChange={(e) =>
                        updateTextGoal("shortTermGoals", row.id, e.target.value)
                      }
                    />
                    {canEdit ? (
                      <button
                        type="button"
                        disabled={(values.shortTermGoals || []).length <= 1}
                        onClick={() =>
                          removeTextGoal("shortTermGoals", "st", row.id)
                        }
                        className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                        aria-label={`Remove short-term goal ${idx + 1}`}
                      >
                        <DeleteIcon size={14} />
                      </button>
                    ) : null}
                  </div>
                ))}
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => addTextGoal("shortTermGoals", "st")}
                    className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
                  >
                    <PlusIcon size={13} />
                    Add Goal
                  </button>
                ) : null}
              </div>

              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Long Term Goal 1 (Discharge)
                </p>
                {(values.longTermGoals || []).map((row, idx) => (
                  <div key={row.id} className="flex gap-2 min-w-0">
                    <span
                      className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700"
                      aria-hidden
                    >
                      {idx + 1}
                    </span>
                    <textarea
                      rows={2}
                      className={`${textareaClass} min-w-0 flex-1`}
                      value={row.text || ""}
                      disabled={!canEdit}
                      placeholder="e.g., Pt will perform ADLs independently…"
                      aria-label={`Long-term goal ${idx + 1}`}
                      onChange={(e) =>
                        updateTextGoal("longTermGoals", row.id, e.target.value)
                      }
                    />
                    {canEdit ? (
                      <button
                        type="button"
                        disabled={(values.longTermGoals || []).length <= 1}
                        onClick={() =>
                          removeTextGoal("longTermGoals", "lt", row.id)
                        }
                        className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                        aria-label={`Remove long-term goal ${idx + 1}`}
                      >
                        <DeleteIcon size={14} />
                      </button>
                    ) : null}
                  </div>
                ))}
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => addTextGoal("longTermGoals", "lt")}
                    className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
                  >
                    <PlusIcon size={13} />
                    Add Goal
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="OT Authentication"
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

OtEvalNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default OtEvalNoteForm;
